"use client";

import { useEffect } from "react";

export default function GlobalModelHost() {
    useEffect(() => {
        // Ensure a single hidden host exists
        let host = document.getElementById("global-model-host");
        if (!host) {
            host = document.createElement("div");
            host.id = "global-model-host";
            host.style.position = "fixed";
            host.style.inset = "0";
            host.style.pointerEvents = "none";
            host.style.visibility = "hidden";
            document.body.appendChild(host);
        }

        // Namespace globals
        if (!window.SmartClip) window.SmartClip = {};
        if (!window.SmartClip.__globalModel) window.SmartClip.__globalModel = null;

        // Create (or reuse) a loader promise only when needed
        function ensureModelViewerLoaded() {
            if (window.customElements && window.customElements.get("model-viewer")) {
                return Promise.resolve();
            }
            if (!window.SmartClip.__modelViewerLibLoading) {
                window.SmartClip.__modelViewerLibLoading = new Promise(function (resolve, reject) {
                    const script = document.createElement("script");
                    script.type = "module";
                    script.crossOrigin = "anonymous";
                    // jsDelivr CDN (fast, global)
                    script.src = "https://cdn.jsdelivr.net/npm/@google/model-viewer@1.12.1/dist/model-viewer.min.js";
                    script.onload = resolve;
                    script.onerror = reject;
                    document.head.appendChild(script);
                }).catch(function () {
                    // Ignore; mount will fail gracefully
                });
            }
            return window.SmartClip.__modelViewerLibLoading || Promise.resolve();
        }

        async function resolveEnvUrl() {
            // Prefer HDR/KTX2 (Babylon .env is not supported)
            const candidates = [
                "/assets/hero/Warehouse.hdr",
                "/assets/hero/Warehouse.ktx2",
            ];
            for (let i = 0; i < candidates.length; i += 1) {
                try {
                    const res = await fetch(candidates[i], { method: "HEAD" });
                    if (res && res.ok) return candidates[i];
                } catch {
                    // continue
                }
            }
            // Known good fallback
            return "https://modelviewer.dev/shared-assets/environments/industrial_sunset_2k.hdr";
        }

        function hidePosterSafely() {
            const poster = document.getElementById("hero-poster");
            if (poster) poster.style.opacity = "0";
        }

        function isMobile() {
            return (
                typeof window !== "undefined" &&
                window.matchMedia &&
                window.matchMedia("(max-width: 767px)").matches
            );
        }

        function enableAutoRotateOnFirstInteraction(el) {
            const handler = () => {
                if (!el.hasAttribute("auto-rotate")) el.setAttribute("auto-rotate", "");
                window.removeEventListener("touchstart", handler);
                window.removeEventListener("click", handler);
                window.removeEventListener("keydown", handler);
            };
            window.addEventListener("touchstart", handler, { once: true, passive: true });
            window.addEventListener("click", handler, { once: true });
            window.addEventListener("keydown", handler, { once: true });
        }

        window.SmartClip.mountGlobalModelInto = async function mountGlobalModelInto(containerId) {
            try {
                await ensureModelViewerLoaded();
                const container = document.getElementById(containerId);
                if (!container) return false;

                const envUrl = await resolveEnvUrl();

                if (!window.SmartClip.__globalModel) {
                    const el = document.createElement("model-viewer");
                    el.setAttribute("src", "/assets/hero/hero.glb");
                    el.setAttribute("alt", "Smart Clip Adapter 3D Model");
                    el.setAttribute("camera-controls", "");
                    // Auto-rotate: desktop on, mobile off until interaction
                    if (!isMobile()) {
                        el.setAttribute("auto-rotate", "");
                    }
                    el.setAttribute("environment-image", envUrl);
                    el.setAttribute("shadow-intensity", "0.8");
                    el.setAttribute("shadow-softness", "0.5");
                    el.setAttribute("disable-zoom", "");
                    el.setAttribute("disable-pan", "");
                    // Use environment lighting only; do not render background
                    el.setAttribute("skybox-image", "");
                    el.setAttribute("exposure", "1.1");
                    el.style.cssText = "width:100%;height:100%;display:block;background:transparent";
                    el.addEventListener("error", function (e) {
                        console.warn("model-viewer error", e);
                    });
                    // Hide poster when the model is ready/visible
                    const onPosterDismiss = () => hidePosterSafely();
                    const onLoad = () => hidePosterSafely();
                    el.addEventListener("poster-dismissed", onPosterDismiss, { once: true });
                    el.addEventListener("load", onLoad, { once: true });
                    setTimeout(hidePosterSafely, 1200); // safety fallback

                    (host || container).appendChild(el);
                    window.SmartClip.__globalModel = el;

                    // If mobile, enable auto-rotate after first interaction
                    if (isMobile()) enableAutoRotateOnFirstInteraction(el);
                } else {
                    // If reusing, still ensure poster gets hidden shortly after mount
                    setTimeout(hidePosterSafely, 600);
                    // Ensure auto-rotate policy respected on reuse
                    const el = window.SmartClip.__globalModel;
                    if (isMobile()) {
                        // Ensure it's off until interaction
                        if (el.hasAttribute("auto-rotate")) el.removeAttribute("auto-rotate");
                        enableAutoRotateOnFirstInteraction(el);
                    } else {
                        if (!el.hasAttribute("auto-rotate")) el.setAttribute("auto-rotate", "");
                    }
                }

                // Ensure environment is applied even if model already existed
                if (window.SmartClip.__globalModel) {
                    const currentEnv = window.SmartClip.__globalModel.getAttribute("environment-image");
                    if (!currentEnv || currentEnv === "neutral") {
                        const ensuredEnv = await resolveEnvUrl();
                        window.SmartClip.__globalModel.setAttribute("environment-image", ensuredEnv);
                        window.SmartClip.__globalModel.setAttribute("skybox-image", ""); // keep bg hidden
                        window.SmartClip.__globalModel.setAttribute("exposure", "1.1");
                    }
                }

                if (!container.isConnected) return false;
                if (window.SmartClip.__globalModel.parentNode !== container) {
                    container.innerHTML = "";
                    container.appendChild(window.SmartClip.__globalModel);
                }
                return true;
            } catch (e) {
                console.warn("GlobalModelHost: mount failed", e);
                return false;
            }
        };
    }, []);

    return null;
}


